import React, { useCallback, useEffect, useState } from 'react';
import { PencilIcon, PlusIcon, TrashIcon, TruckIcon } from '@heroicons/react/24/outline';
import { travelAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import type { SaveVehicleRequest, Vehicle } from '../../types/travel';
import Modal from '../ui/Modal';
import DeleteConfirmModal from '../ui/DeleteConfirmModal';
import EmptyState from '../ui/EmptyState';
import { getErrorMessage } from '../../utils/errorMessage';

const emptyForm: SaveVehicleRequest = { name: '', seats: 8, licensePlate: '', notes: '', isActive: true };

const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/** A klub járművei (csapatbuszok stb.), amiket az utazásokhoz be lehet osztani. */
const VehiclesTab: React.FC = () => {
  const { showToast } = useToast();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<SaveVehicleRequest>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<Vehicle | null>(null);

  const load = useCallback(async () => {
    try {
      setVehicles(await travelAPI.getVehicles(true));
    } catch (err) {
      console.error('A járművek betöltése nem sikerült:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openForm = (vehicle: Vehicle | null) => {
    setEditing(vehicle);
    setForm(
      vehicle
        ? {
            name: vehicle.name,
            seats: vehicle.seats,
            licensePlate: vehicle.licensePlate ?? '',
            notes: vehicle.notes ?? '',
            isActive: vehicle.isActive,
          }
        : emptyForm
    );
    setFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...form,
        name: form.name.trim(),
        licensePlate: form.licensePlate?.trim() || null,
        notes: form.notes?.trim() || null,
      };
      if (editing) {
        await travelAPI.updateVehicle(editing.id, data);
      } else {
        await travelAPI.createVehicle(data);
      }
      showToast('Jármű elmentve.');
      setFormOpen(false);
      load();
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      const firstError = errors ? (Object.values(errors)[0] as string[] | undefined)?.[0] : undefined;
      showToast(err.response?.data?.message || firstError || 'A mentés nem sikerült.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await travelAPI.deleteVehicle(deleting.id);
      showToast('Jármű törölve.');
      setDeleting(null);
      load();
    } catch (err: any) {
      showToast(getErrorMessage(err, 'A törlés nem sikerült.'), 'error');
    }
  };

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => openForm(null)}
          className="inline-flex items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
        >
          <PlusIcon className="mr-2 h-5 w-5" />
          Új jármű
        </button>
      </div>

      {loading ? (
        <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow-sm">Betöltés...</div>
      ) : vehicles.length === 0 ? (
        <div className="rounded-lg bg-white shadow-sm">
          <EmptyState
            icon={TruckIcon}
            title="Még nincs jármű"
            description="Vedd fel a klub csapatbuszait, kisbuszait, hogy az utazásoknál be lehessen osztani őket."
            actionLabel="Új jármű"
            onAction={() => openForm(null)}
          />
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {vehicles.map((vehicle) => (
            <li
              key={vehicle.id}
              className={`rounded-lg bg-white p-4 shadow-sm ${vehicle.isActive ? '' : 'opacity-60'}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <TruckIcon className="h-5 w-5 shrink-0 text-gray-500" />
                    <span className="truncate font-semibold text-gray-900">{vehicle.name}</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600">
                    {vehicle.seats} utashely
                    {vehicle.licensePlate && <span className="ml-2 font-mono text-xs text-gray-500">{vehicle.licensePlate}</span>}
                  </p>
                  {vehicle.notes && <p className="mt-1 text-xs text-gray-500">{vehicle.notes}</p>}
                  {!vehicle.isActive && (
                    <span className="mt-2 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">
                      Inaktív
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => openForm(vehicle)}
                    aria-label={`${vehicle.name} szerkesztése`}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-primary-600"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(vehicle)}
                    aria-label={`${vehicle.name} törlése`}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-red-600"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal isOpen={formOpen} onClose={() => setFormOpen(false)} title={editing ? 'Jármű szerkesztése' : 'Új jármű'}>
        <form onSubmit={handleSubmit} className="space-y-4 pb-4">
          <div>
            <label htmlFor="vehicle-name" className="mb-1 block text-sm font-medium text-gray-700">
              Név
            </label>
            <input
              id="vehicle-name"
              required
              maxLength={100}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Pl. Nagy csapatbusz"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="vehicle-seats" className="mb-1 block text-sm font-medium text-gray-700">
                Utashely
              </label>
              <input
                id="vehicle-seats"
                type="number"
                required
                min={1}
                max={100}
                value={form.seats || ''}
                onChange={(e) => setForm({ ...form, seats: Number(e.target.value) })}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">A sofőrön kívül.</p>
            </div>
            <div>
              <label htmlFor="vehicle-plate" className="mb-1 block text-sm font-medium text-gray-700">
                Rendszám
              </label>
              <input
                id="vehicle-plate"
                maxLength={20}
                value={form.licensePlate ?? ''}
                onChange={(e) => setForm({ ...form, licensePlate: e.target.value })}
                className={`${inputClass} uppercase`}
              />
            </div>
          </div>
          <div>
            <label htmlFor="vehicle-notes" className="mb-1 block text-sm font-medium text-gray-700">
              Megjegyzés
            </label>
            <textarea
              id="vehicle-notes"
              rows={2}
              maxLength={500}
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={inputClass}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Aktív (választható új utazáshoz)
          </label>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => setFormOpen(false)}
              className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Mégse
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Mentés...' : 'Mentés'}
            </button>
          </div>
        </form>
      </Modal>

      <DeleteConfirmModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Jármű törlése"
        message="Biztosan törlöd ezt a járművet? A már megszervezett utazásokban a neve és a férőhelye megmarad. Ha csak ideiglenesen nem használható, inkább állítsd inaktívra."
        itemName={deleting?.name ?? ''}
      />
    </div>
  );
};

export default VehiclesTab;
